import { useRef, useCallback } from "react";
import { EventStreamCodec, Message } from "@aws-sdk/eventstream-codec";
import { fromUtf8, toUtf8 } from "@aws-sdk/util-utf8-browser";

const codec = new EventStreamCodec(toUtf8, fromUtf8);

interface TranscriptEvent {
  type: "transcript";
  text: string;
  isPartial: boolean;
  alternatives?: Array<{ transcript: string; confidence: number }>;
}

interface UseDirectTranscribeProps {
  onTranscript: (event: TranscriptEvent) => void;
  onStatusChange: (status: string) => void;
  onError: (error: Error) => void;
}

export const useDirectTranscribe = ({
  onTranscript,
  onStatusChange,
  onError,
}: UseDirectTranscribeProps) => {
  const socketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const keepAliveRef = useRef<NodeJS.Timeout | null>(null);

  const isMicActiveRef = useRef(false);

  // ─────────────────────────────────────────────
  // CONNECT (ONLY ONCE)
  // ─────────────────────────────────────────────
  const connect = useCallback(
    (url: string) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        console.log("⚡ Reusing existing WebSocket");
        return;
      }

      try {
        onStatusChange("Connecting...");

        const socket = new WebSocket(url);
        socket.binaryType = "arraybuffer";
        socketRef.current = socket;

        socket.onopen = () => {
          console.log("✅ WebSocket connected");
          onStatusChange("Connected");

          // Keep alive (send silence every 8s ONLY when NOT recording)
          keepAliveRef.current = setInterval(() => {
            if (socket.readyState === WebSocket.OPEN && !isMicActiveRef.current) {
              const silence = new Int16Array(1600);
              sendAudioEvent(silence.buffer);
            }
          }, 8000);
        };

        socket.onmessage = (event) => {
          try {
            const message = codec.decode(new Uint8Array(event.data));
            const eventType = message.headers[":event-type"]?.value;
            const messageType = message.headers[":message-type"]?.value;

            if (messageType === "exception") {
              const body = JSON.parse(new TextDecoder().decode(message.body));
              onError(new Error(body.Message));
              return;
            }

            if (eventType === "TranscriptEvent") {
              const body = JSON.parse(new TextDecoder().decode(message.body));

              body.Transcript?.Results?.forEach((result: any) => {
                if (result.Alternatives?.length > 0) {
                  const text = result.Alternatives[0].Transcript;
                  if (text?.trim()) {
                    onTranscript({
                      type: "transcript",
                      text,
                      isPartial: result.IsPartial,
                    });
                  }
                }
              });
            }
          } catch (err) {
            console.error("Parse error:", err);
          }
        };

        socket.onclose = () => {
          console.log("🔌 WebSocket closed");
          onStatusChange("Disconnected");

          if (keepAliveRef.current) {
            clearInterval(keepAliveRef.current);
            keepAliveRef.current = null;
          }
        };

        socket.onerror = (err) => {
          console.error("WS error:", err);
          onStatusChange("Error");
        };
      } catch (err) {
        onError(err as Error);
      }
    },
    [onTranscript, onStatusChange, onError],
  );

  // ─────────────────────────────────────────────
  // AUDIO PROCESSING & 16kHz DOWNSAMPLING
  // ─────────────────────────────────────────────
  const downsampleTo16k = (
    inputBuffer: Float32Array,
    inputSampleRate: number,
  ): ArrayBuffer => {
    if (inputSampleRate === 16000) {
      const output = new Int16Array(inputBuffer.length);
      for (let i = 0; i < inputBuffer.length; i++) {
        const s = Math.max(-1, Math.min(1, inputBuffer[i]));
        output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      return output.buffer;
    }

    const sampleRateRatio = inputSampleRate / 16000;
    const newLength = Math.round(inputBuffer.length / sampleRateRatio);
    const result = new Int16Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;

    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
      let accum = 0;
      let count = 0;
      for (
        let i = offsetBuffer;
        i < nextOffsetBuffer && i < inputBuffer.length;
        i++
      ) {
        accum += inputBuffer[i];
        count++;
      }
      const sample = count > 0 ? accum / count : inputBuffer[offsetBuffer] || 0;
      const clamped = Math.max(-1, Math.min(1, sample));
      result[offsetResult] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }
    return result.buffer;
  };

  const sendAudioEvent = (pcmBuffer: ArrayBuffer) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN)
      return;

    const audioMessage: Message = {
      headers: {
        ":message-type": { type: "string", value: "event" },
        ":event-type": { type: "string", value: "AudioEvent" },
        ":content-type": { type: "string", value: "application/octet-stream" },
      },
      body: new Uint8Array(pcmBuffer),
    };

    socketRef.current.send(codec.encode(audioMessage));
  };

  // ─────────────────────────────────────────────
  // START MICROPHONE
  // ─────────────────────────────────────────────
  const startMicrophone = useCallback(async (): Promise<MediaStream> => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const AudioContextClass =
      window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioContextClass();
    audioContextRef.current = audioContext;

    const actualSampleRate = audioContext.sampleRate;
    console.log(
      `🎤 Microphone AudioContext running at ${actualSampleRate}Hz (resampling to 16000Hz)`,
    );

    const source = audioContext.createMediaStreamSource(stream);
    sourceRef.current = source;

    // Use 2048 buffer for stable processing without dropping frames
    const processor = audioContext.createScriptProcessor(2048, 1, 1);
    processorRef.current = processor;

    isMicActiveRef.current = true;

    processor.onaudioprocess = (e) => {
      if (!isMicActiveRef.current) return;
      const data = e.inputBuffer.getChannelData(0);
      const pcm = downsampleTo16k(data, actualSampleRate);
      sendAudioEvent(pcm);
    };

    source.connect(processor);
    processor.connect(audioContext.destination);

    return stream;
  }, []);

  // ─────────────────────────────────────────────
  // STOP MICROPHONE ONLY
  // ─────────────────────────────────────────────
  const stopMicrophone = useCallback(() => {
    isMicActiveRef.current = false;

    // Send a brief block of silence (200ms) to cleanly signal end of utterance to AWS Transcribe
    // so AWS finalizes the last spoken sentence instead of dropping it
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      const flushSilence = new Int16Array(3200); // 200ms of 16kHz silence
      sendAudioEvent(flushSilence.buffer);
    }

    processorRef.current?.disconnect();
    sourceRef.current?.disconnect();
    audioContextRef.current?.close();

    processorRef.current = null;
    sourceRef.current = null;
    audioContextRef.current = null;
  }, []);

  // ─────────────────────────────────────────────
  // FULL DISCONNECT (END SESSION)
  // ─────────────────────────────────────────────
  const disconnect = useCallback(() => {
    stopMicrophone();

    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
  }, [stopMicrophone]);

  return {
    connect,
    disconnect,
    startMicrophone,
    stopMicrophone,
    isConnected: socketRef.current?.readyState === WebSocket.OPEN,
  };
};
